import { useState, useEffect, useCallback } from 'react'
import { EffectData } from '@/lib/magnumMoment'

function parseConfig(config: string | null): Record<string, any> {
  if (!config) return {}
  try { return JSON.parse(config) } catch { return {} }
}

function ConfettiEffect({ config, onDone }: { config: Record<string, any>; onDone: () => void }) {
  const duration = config.duration || 3000
  const [particles] = useState(() =>
    Array.from({ length: 60 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 500,
      color: ['#ff6b6b', '#feca57', '#48dbfb', '#ff9ff3', '#54a0ff', '#5f27cd', '#01a3a4', '#f368e0'][i % 8],
      size: 6 + Math.random() * 8,
    }))
  )

  useEffect(() => {
    const timer = setTimeout(onDone, duration)
    return () => clearTimeout(timer)
  }, [duration, onDone])

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute animate-confetti-fall"
          style={{
            left: `${p.left}%`,
            top: -20,
            width: p.size,
            height: p.size * 0.6,
            backgroundColor: p.color,
            borderRadius: 2,
            animationDelay: `${p.delay}ms`,
            animationDuration: `${duration}ms`,
          }}
        />
      ))}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        .animate-confetti-fall {
          animation: confetti-fall linear forwards;
        }
      `}</style>
    </div>
  )
}

function ScreenShakeEffect({ config, onDone }: { config: Record<string, any>; onDone: () => void }) {
  const duration = config.duration || 800

  useEffect(() => {
    document.documentElement.style.animation = `ee-shake ${duration}ms ease-in-out`
    const style = document.createElement('style')
    style.textContent = `
      @keyframes ee-shake {
        0%, 100% { transform: translate(0); }
        10% { transform: translate(-4px, 2px); }
        20% { transform: translate(4px, -2px); }
        30% { transform: translate(-3px, 3px); }
        40% { transform: translate(3px, -1px); }
        50% { transform: translate(-2px, 2px); }
        60% { transform: translate(2px, -2px); }
        70% { transform: translate(-1px, 1px); }
        80% { transform: translate(1px, -1px); }
        90% { transform: translate(-1px, 0); }
      }
    `
    document.head.appendChild(style)

    const timer = setTimeout(() => {
      document.documentElement.style.animation = ''
      style.remove()
      onDone()
    }, duration)

    return () => {
      document.documentElement.style.animation = ''
      style.remove()
      clearTimeout(timer)
    }
  }, [duration, onDone])

  return null
}

function ColorInvertEffect({ config, onDone }: { config: Record<string, any>; onDone: () => void }) {
  const duration = config.duration || 2000

  useEffect(() => {
    document.documentElement.style.filter = 'invert(1) hue-rotate(180deg)'
    document.documentElement.style.transition = 'filter 0.3s ease'

    const timer = setTimeout(() => {
      document.documentElement.style.filter = ''
      setTimeout(() => {
        document.documentElement.style.transition = ''
        onDone()
      }, 300)
    }, duration)

    return () => {
      document.documentElement.style.filter = ''
      document.documentElement.style.transition = ''
      clearTimeout(timer)
    }
  }, [duration, onDone])

  return null
}

function AbsurdWordEffect({ config, onDone }: { config: Record<string, any>; onDone: () => void }) {
  const word = config.word || 'supercalifragilisticexpialidocious'
  const message = config.message || 'Good luck with this one!'
  const duration = config.duration || 4000

  useEffect(() => {
    const timer = setTimeout(onDone, duration)
    return () => clearTimeout(timer)
  }, [duration, onDone])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-card rounded-2xl p-8 max-w-lg mx-4 text-center space-y-4 animate-bounce-in">
        <p className="text-xl font-bold text-primary break-all">{word}</p>
        <p className="text-muted-foreground">{message}</p>
      </div>
      <style>{`
        @keyframes bounce-in {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.05); }
          70% { transform: scale(0.95); }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-bounce-in { animation: bounce-in 0.5s ease-out; }
      `}</style>
    </div>
  )
}

function EarlyReleaseEffect({ config, onDone }: { config: Record<string, any>; onDone: () => void }) {
  const message = config.message || 'Psst... take a break!'
  const duration = config.duration || 5000

  useEffect(() => {
    const timer = setTimeout(onDone, duration)
    return () => clearTimeout(timer)
  }, [duration, onDone])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-2xl p-8 max-w-sm mx-4 text-center space-y-3">
        <p className="text-4xl">🤫</p>
        <p className="text-lg font-medium">{message}</p>
        <button onClick={onDone} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
          Nah, I'm good
        </button>
      </div>
    </div>
  )
}

function BirthdayCountdownEffect({ config, onDone }: { config: Record<string, any>; onDone: () => void }) {
  const [daysUntil, setDaysUntil] = useState<number | null>(null)

  useEffect(() => {
    const birthDate = config.birthDate
    if (!birthDate) { onDone(); return }

    const birth = new Date(birthDate)
    const now = new Date()
    const nextBirthday = new Date(now.getFullYear(), birth.getMonth(), birth.getDate())
    if (nextBirthday <= now) nextBirthday.setFullYear(nextBirthday.getFullYear() + 1)
    const days = Math.ceil((nextBirthday.getTime() - now.getTime()) / 86400000)
    setDaysUntil(days)

    const timer = setTimeout(onDone, 4000)
    return () => clearTimeout(timer)
  }, [config.birthDate, onDone])

  if (daysUntil === null) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-2xl p-8 max-w-sm mx-4 text-center space-y-3">
        <p className="text-4xl">🎂</p>
        <p className="text-lg font-medium">
          {daysUntil === 0
            ? 'HAPPY BIRTHDAY!!!'
            : `It's ${daysUntil} days until your birthday!`}
        </p>
      </div>
    </div>
  )
}

const EFFECT_COMPONENTS: Record<string, React.ComponentType<{ config: Record<string, any>; onDone: () => void }>> = {
  CONFETTI: ConfettiEffect,
  SCREEN_SHAKE: ScreenShakeEffect,
  COLOR_INVERT: ColorInvertEffect,
  ABSURD_WORD: AbsurdWordEffect,
  EARLY_RELEASE: EarlyReleaseEffect,
  BIRTHDAY_COUNTDOWN: BirthdayCountdownEffect,
}

export default function EffectRenderer({ effects, onDone }: { effects: EffectData[] | null; onDone: () => void }) {
  const [currentIndex, setCurrentIndex] = useState(0)

  useEffect(() => {
    setCurrentIndex(0)
  }, [effects])

  const handleEffectDone = useCallback(() => {
    setCurrentIndex(prev => {
      const next = prev + 1
      if (!effects || next >= effects.length) {
        onDone()
      }
      return next
    })
  }, [effects, onDone])

  if (!effects || effects.length === 0 || currentIndex >= effects.length) return null

  const effect = effects[currentIndex]
  const config = parseConfig(effect.config)
  const EffectComponent = EFFECT_COMPONENTS[effect.effectType]

  if (!EffectComponent) {
    handleEffectDone()
    return null
  }

  return <EffectComponent config={config} onDone={handleEffectDone} />
}
